import {
  Badge,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  ArrowsOutSimple,
  CheckSquare,
  Paperclip,
  X,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useProcessForm } from '../ProcessesTab';

import pdfIcon from '~/assets/svg/attachmentPdfIcon.svg';
import otherFileTypeIcon from '~/assets/svg/otherFileType.svg';
import videoIcon from '~/assets/svg/videoIcon.svg';
import { axiosInstanceForPublicAPI } from '~/assets/utils/axiosInstance';
import AttachmentPopUp from '~/components/customer/report/reportDocuments/attachments/attachmentPopup';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { FilePicker, FilePickerDropzone } from '~/components/ui/file-picker';
import { useFormController } from '~/components/ui/form';
import { Tab, TabList, TabPanel, Tabs } from '~/components/ui/tabs';
import {
  PDF_FILE_TYPE,
  DOC_FILE_TYPES,
  IMAGE_FILE_TYPES,
  VIDEO_FILE_TYPES,
} from '~/constants/fileTypeConstants';
import {
  PDF_MIME_TYPE,
  DOC_MIME_TYPE,
  IMAGE_MIME_TYPES,
  VIDEO_MIME_TYPES,
} from '~/constants/mimeTypeConstants';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { useAppSelector } from '~/hooks/global/typedReduxHooks';
import { FileUploadService } from '~/services/api/file-upload/endpoint';
import { ProcessService } from '~/services/api/processes/endpoints';
import { AttachmentItem } from '~/services/api/processes/types';
import QueryResolver from '~/services/api/query-resolver';
import { isMutationSuccess } from '~/services/api/rest-api';

type AttachmentType = {
  file_id: number;
  file_name: string;
  is_uploaded: boolean;
};

export function AttachFilebutton() {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const { referenceId } = useAppSelector((state) => state.reportData.report);
  const mainForm = useProcessForm();
  const workflowId = mainForm.watch('workflowId');
  const controller = useFormController();
  const files = Array.isArray(controller?.field.value)
    ? (controller?.field.value as AttachmentType[])
    : ([] as AttachmentType[]);

  const attachmentsQuery =
    ProcessService.endpoints.listAllReportAttachments.useQuery(
      {
        workflow_id: workflowId ?? -1,
        params: {
          reference_id: referenceId,
        },
      },
      { skip: !workflowId },
    );

  const setIdsForAttachments = (id: number, name: string) => {
    // adds file in array if not in array and remove it if it exists
    let updatedFiles = [...files];
    const fileUpload = updatedFiles.find((file) => file.file_id === id);
    if (fileUpload) {
      updatedFiles = updatedFiles.filter((file) => file.file_id !== id);
    } else {
      updatedFiles.push({
        file_id: id,
        file_name: name,
        is_uploaded: false,
      });
    }
    controller?.field.onChange(updatedFiles);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen} isDismissable={false}>
      <DialogTrigger asChild>
        <Tooltip title={intl.formatMessage({ defaultMessage: 'Attach File' })}>
          <IconButton>
            <Badge badgeContent={files?.length}>
              <Paperclip />
            </Badge>
          </IconButton>
        </Tooltip>
      </DialogTrigger>
      <DialogContent size='3xl' className='flex flex-col gap-4'>
        <div className='flex justify-between'>
          <DialogTitle>
            <FormattedMessage defaultMessage='Attach File' />
          </DialogTitle>
          <DialogClose>
            <IconButton
              onClick={() => {
                setIsOpen(false);
              }}
            >
              <X />
            </IconButton>
          </DialogClose>
        </div>
        <Tabs defaultValue='upload-file'>
          <TabList>
            <Tab value='upload-file'>
              <Typography>
                <FormattedMessage defaultMessage='Upload Files' />
              </Typography>
            </Tab>
            <Tab value='select-file'>
              <Typography>
                <FormattedMessage defaultMessage='Choose From Files' />
              </Typography>
            </Tab>
          </TabList>
          <TabPanel value='upload-file'>
            <UploadFiles />
          </TabPanel>
          <TabPanel value='select-file'>
            <QueryResolver query={attachmentsQuery}>
              {(data) => (
                <div className='flex flex-col gap-4'>
                  <div>
                    <Typography>
                      <FormattedMessage defaultMessage='Choose from files uploaded on this report' />
                    </Typography>
                  </div>
                  <div className='flex gap-2'>
                    {data.map((attachment) => (
                      <div
                        key={attachment.id}
                        onClick={() =>
                          setIdsForAttachments(
                            attachment.id,
                            attachment.original_file_name,
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            setIdsForAttachments(
                              attachment.id,
                              attachment.original_file_name,
                            );
                          }
                        }}
                        role='button'
                        tabIndex={0}
                        aria-label='add attachment'
                      >
                        <ReportAttachmentItem
                          added={
                            !!files?.find(
                              (file) => file.file_id === attachment.id,
                            )
                          }
                          {...attachment}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </QueryResolver>
          </TabPanel>
        </Tabs>
        <Button onClick={() => setIsOpen(false)}>
          <FormattedMessage defaultMessage='Attach' />
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function UploadFiles() {
  const controller = useFormController();
  const files = Array.isArray(controller?.field.value)
    ? (controller?.field.value as AttachmentType[])
    : ([] as AttachmentType[]);

  const [uploadFile, state] =
    FileUploadService.endpoints.uploadFileForProcessComment.useMutation();

  const processFiles = async (uploadedFiles: File[]) => {
    const promiseAll = uploadedFiles.map(async (file) => {
      const upload = await uploadFile({
        file: file,
        startUploadData: {
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
        },
      });
      if (isMutationSuccess(upload)) {
        return {
          file_id: upload.data.id,
          file_name: file.name,
          is_uploaded: true,
        };
      }
    });
    const results = await Promise.all(promiseAll);
    controller?.field.onChange([...files, ...results]);
  };

  const removeFile = (fileId: number) => {
    controller?.field.onChange(files?.filter((f) => f.file_id !== fileId));
  };

  return (
    <div>
      <FilePicker
        maxFiles={Infinity}
        onFileAccept={(details) => processFiles(details.files)}
      >
        <FilePickerDropzone />
        {state.isLoading ? (
          <LinearProgress />
        ) : (
          <div>
            {files
              ?.filter((file) => file.is_uploaded)
              .map((file) => (
                <FileForUpload
                  key={file.file_id}
                  item={{
                    id: file?.file_id,
                    name: file?.file_name,
                  }}
                  removeFile={removeFile}
                />
              ))}
          </div>
        )}
      </FilePicker>
    </div>
  );
}

function FileForUpload({
  item,
  removeFile,
}: {
  item: {
    id: number;
    name: string;
  };
  removeFile: (fileId: number) => void;
}) {
  return (
    <div>
      <div className='grid gap-2 p-2'>
        <>
          <div className='flex items-center gap-2'>
            <div className='max-w-64 w-full flex flex-col space-y-0.5'>
              <Typography level='body-sm' className='truncate'>
                {item.name}
              </Typography>
            </div>
            <div className='ml-auto'>
              <Button
                size='sm'
                variant='plain'
                color='danger'
                onClick={() => {
                  removeFile(item.id);
                }}
              >
                <FormattedMessage defaultMessage='Remove File' />
              </Button>
            </div>
          </div>
        </>
      </div>
    </div>
  );
}

export function ReportAttachmentItem({
  added,
  ...item
}: AttachmentItem & { added?: boolean }) {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const [viewFile, setViewFile] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const userAccessRoles = useAppSelector(
    (state) => state.reportData.userAccessRoles,
  );

  const viewFileToggle = () => setViewFile((prev) => !prev);
  const onHover = () => {
    setIsHovered(true);
  };

  const exitHover = () => {
    setIsHovered(false);
  };

  const getIcon = (currfileType: string) => {
    if (IMAGE_FILE_TYPES.includes(currfileType)) return item.url;
    if (IMAGE_MIME_TYPES.includes(currfileType)) return item.url;
    if (VIDEO_FILE_TYPES.includes(currfileType)) return videoIcon;
    if (VIDEO_MIME_TYPES.includes(currfileType)) return videoIcon;
    if (DOC_FILE_TYPES.includes(currfileType)) return otherFileTypeIcon;
    if (DOC_MIME_TYPE === currfileType) return otherFileTypeIcon;
    if (PDF_FILE_TYPE === currfileType) return pdfIcon;
    if (PDF_MIME_TYPE === currfileType) return pdfIcon;

    return otherFileTypeIcon;
  };

  //DOWNLOAD FILE

  const downloadAttachment = async () => {
    if (userAccessRoles.is_allowed_to_download_attachments) {
      try {
        axiosInstanceForPublicAPI()
          .get(item.url, { responseType: 'blob' })
          .then((response) => {
            // Create blob link to download
            const downloadUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', item.original_file_name);
            // Append to html link element page
            document.body.appendChild(link);
            // Start download
            link.click();
            // Clean up and remove the link
            if (link.parentNode) link.parentNode.removeChild(link);
            createToastNotification(
              'Attachment downloaded successfully.',
              'success',
            );
          })
          .catch(() => {
            createToastNotification(
              intl.formatMessage({ defaultMessage: 'Something went wrong ' }),
              'error',
            );
          });
      } catch (downloadError: unknown) {
        createToastNotification(
          intl.formatMessage({ defaultMessage: 'Something went wrong' }),
          'error',
        );
      }
    } else {
      createToastNotification(
        intl.formatMessage({
          defaultMessage:
            'You are not permitted to download any attachments files on this report.',
        }),
        'info',
      );
      return;
    }
  };

  return (
    <>
      <Card
        className='relative w-24 border-2 !p-1'
        onMouseOver={onHover}
        onMouseOut={exitHover}
      >
        <div className='absolute top-1 left-1'>
          {added && (
            <CheckSquare
              weight='fill'
              className='text-brand-primary-600 bg-white'
            />
          )}
        </div>
        {isHovered && (
          <div className='absolute top-1 right-1'>
            <IconButton
              variant='solid'
              size='sm'
              onClick={viewFileToggle}
              color='secondary'
            >
              <ArrowsOutSimple weight='fill' />
            </IconButton>
          </div>
        )}
        <img
          src={getIcon(item.file_type)}
          alt={item.original_file_name}
          className='w-24'
        />
      </Card>
      {viewFile && (
        <AttachmentPopUp
          handleClose={viewFileToggle}
          download={downloadAttachment}
          fileType={item.file_type}
          url={item.url}
          fileName={item.original_file_name}
        />
      )}
    </>
  );
}
